/**
 * The cross-section of a sweep or loft section: drawn `profile` points or one
 * of the shorthands (ellipse radii, rounded rect, circle radius), exactly as
 * prism's `profile_points` picks them (profile ≥ 3 points > rect > ellipse >
 * radius).
 */

import { ProfileEditor } from '../curves/ProfileEditor.js';
import { profilePoints, tidyPoint, type Vec2, type Vec3 } from '../curves/curve-math.js';
import { Enum, num, Num, patch, vec, Vec, type Obj } from './fields.js';
import { points2 } from './LatheEditor.js';

export type ProfileMode = 'profile' | 'rect' | 'ellipse' | 'radius' | 'inherit';

/** Which profile source the fields select (same precedence as prism). */
export function profileMode(o: Obj, radiusIsDefault: boolean): ProfileMode {
  if (points2(o.profile).length >= 3) return 'profile';
  if (vec(o.rect, 3)) return 'rect';
  if (vec(o.ellipse, 2)) return 'ellipse';
  if (radiusIsDefault || num(o.radius) !== undefined) return 'radius';
  return 'inherit';
}

/** Typed `[x, y, z]` points (bad entries dropped). */
export function points3(v: unknown): Vec3[] {
  return (Array.isArray(v) ? v : []).flatMap((p) =>
    Array.isArray(p) && [0, 1, 2].every((i) => typeof p[i] === 'number') ? [[p[0], p[1], p[2]] as Vec3] : [],
  );
}

/** The closed outline the fields describe (null when none: a loft section reusing the previous one). */
export function resolvedProfile(o: Obj, segments: number, smooth: boolean, defaultRadius?: number): Vec2[] | null {
  return profilePoints(points2(o.profile), vec(o.ellipse, 2), vec(o.rect, 3), num(o.radius) ?? defaultRadius, segments, smooth);
}

/** Switch the profile source, converting the current outline where useful. */
export function setProfileMode<T extends Obj>(o: T, mode: ProfileMode, defaultRadius?: number): T {
  const current = resolvedProfile(o, 8, false, defaultRadius);
  const ext = current?.reduce((m, p) => Math.max(m, Math.abs(p[0]), Math.abs(p[1])), 0) || 0.1;
  let next: Obj = { ...o };
  for (const k of ['profile', 'rect', 'ellipse']) delete next[k];
  if (defaultRadius === undefined) delete next.radius;
  if (mode === 'profile') {
    const pts = current && current.length >= 3 ? current : profilePoints(undefined, undefined, undefined, ext, 8, false)!;
    next.profile = pts.map(tidyPoint);
  } else if (mode === 'rect') next.rect = [ext * 2, ext * 2, 0];
  else if (mode === 'ellipse') next.ellipse = [ext, ext];
  else if (mode === 'radius') next = patch(next, 'radius', num(o.radius) ?? ext);
  return next as T;
}

const MODE_OPTIONS = [
  { value: 'profile', label: 'Drawn points' },
  { value: 'radius', label: 'Circle' },
  { value: 'ellipse', label: 'Ellipse' },
  { value: 'rect', label: 'Rounded rectangle' },
];

/**
 * Mode select + the mode's fields + the profile canvas. `defaultRadius`: the
 * owner always has a radius (sweep); without it the "previous section"
 * choice appears (loft).
 */
export function ProfileSourceFields({
  value,
  onChange,
  segments,
  smooth,
  axes,
  defaultRadius,
}: {
  value: Obj;
  onChange: (next: Obj) => void;
  segments: number;
  smooth: boolean;
  axes: [string, string];
  defaultRadius?: number;
}) {
  const mode = profileMode(value, defaultRadius !== undefined);
  const set = (key: string, v: unknown) => onChange(patch(value, key, v));
  const pts = points2(value.profile);
  return (
    <>
      <Enum
        label="Profile"
        hint="Closed cross-section: drawn points, or a circle / ellipse / rounded rectangle shorthand."
        value={mode}
        options={defaultRadius === undefined ? [...MODE_OPTIONS, { value: 'inherit', label: 'Same as previous' }] : MODE_OPTIONS}
        onChange={(m) => onChange(setProfileMode(value, (m ?? 'radius') as ProfileMode, defaultRadius))}
      />
      {mode === 'profile' && (
        <ProfileEditor
          closed
          axes={axes}
          points={pts}
          smooth={smooth}
          samples={Math.max(2, Math.floor(Math.min(512, Math.max(3, segments)) / Math.max(1, pts.length)))}
          height={180}
          onChange={(p) => set('profile', p)}
        />
      )}
      {mode === 'radius' && (
        <Num label="Radius" hint="Circle profile radius (m)." min={0} value={num(value.radius) ?? defaultRadius} onChange={(v) => set('radius', v)} />
      )}
      {mode === 'ellipse' && (
        <Vec label="Ellipse" hint="Ellipse radii (m)." size={2} labels={['A', 'B']} value={vec(value.ellipse, 2)} onChange={(v) => set('ellipse', v)} />
      )}
      {mode === 'rect' && (
        <Vec
          label="Rectangle"
          hint="Rounded rectangle [width, height, corner radius] (m)."
          size={3}
          labels={['W', 'H', 'R']}
          value={vec(value.rect, 3)}
          onChange={(v) => set('rect', v)}
        />
      )}
    </>
  );
}
